<script setup lang="ts">
import {computed, reactive, ref} from "vue";
import {Avatar, Button, Icon} from "@/Components/Library";
import {Comments, PageLoading, PageTitle} from "@/Components/Global";
import QuestionComponents from "@/Pages/User/Questions/Question/Types";
import {useRoute, useRouter} from "vue-router";
import {useStorage} from "@vueuse/core";
import StimulusDisplay from "../Stimuli/StimulusDisplay.vue";
import GroupDrawer from "@/Pages/User/Groups/GroupDrawer.vue";
import QuestionResponsesTable from "@/Pages/User/Questions/Question/Responses/QuestionResponsesTable.vue";
import QuestionResponsesChart from "@/Pages/User/Questions/Question/Responses/QuestionResponsesChart.vue";
import QuestionResponsesWordCloud from "@/Pages/User/Questions/Question/Responses/QuestionResponsesWordCloud.vue";
import QuestionResponsesVoting from "@/Pages/User/Questions/Question/Responses/QuestionResponsesVoting.vue";
import {duplicateQuestion, getQuestion, getQuestionPreview} from "@/Services/questions";
import User from "@/Models/User";
import LaunchHeader from "./Launch/LaunchHeader.vue";
import Question from "@/Models/Question";
import {useToast} from "primevue/usetoast";
import {useConfirm} from "primevue/useconfirm";
import Str from "@/Utils/Str";
import QuestionLaunchToGroupDrawer from "@/Pages/User/Questions/Form/QuestionLaunchToGroupDrawer.vue";
import QuestionActionsDrawer from "@/Pages/User/Questions/Form/QuestionActionsDrawer.vue";
import {getGroup} from "@/Services/groups.ts";
import {useMainStore} from "@/Stores/store.ts";
import markdownit from "markdown-it";

const $route = useRoute();
const $store = useMainStore();
const $user = new User();
const router = useRouter();
const toast = useToast();
const confirm = useConfirm();
const md = markdownit();

const cancel = () => {
    router.push(
        router.currentRoute.value.query.from === "form"
            ? {
                  name: question.value?.id ? "questions.edit" : "questions.create",
                  params: question.value?.id ? {question: question.value.id} : {},
              }
            : {name: "questions.index"},
    );
};

const state = reactive({
    show: {
        group: null,
        question: null,
        submittedPopup: false,
        launchToGroupDrawer: false,
        actionsDrawer: false,
    },
    loading: {
        question: true,
    },
});

const questionLocal = useStorage(`U${$user.id}:questionForm`, {});
const question = ref();
const response = ref({data: null});
const transformQuestion = () => {
    let {title, type, question_id, stimulus_id, params, options, stock_image} = questionLocal.value;
    return {
        title,
        type,
        question_id,
        stimulus_id,
        params,
        options,
        stock_image,
        tags: questionLocal.value.tags?.map((tag) => tag.name),
    };
};
const resetResponse = (e = null) => {
    switch (question.value.type.slug) {
        case "multiple_choice":
            response.value.data = question.value.param("matrix")
                ? {}
                : question.value.param("allow_multiple")
                  ? []
                  : null;
            break;
        case "word":
            response.value.data = question.value.param("allow_multiple") ? [""] : null;
            break;
        case "ranking":
            response.value.data = question.value.options.map((O) => O.id);
            break;
        case "weighting":
            let opts = new Map(question.value.options.map((O) => [O.id, 0]));
            response.value.data = Object.fromEntries(opts);
            break;
        case "text":
            response.value.data = {
                votes: {},
                content: "",
            };
            break;
        default:
            response.value.data = null;
            break;
    }
};

if ($route.params.question) {
    getQuestion($route.params.question, {preview: true}).then(({data}) => {
        question.value = new Question(data);
        resetResponse();
        state.loading.question = false;
    });
} else {
    getQuestionPreview(transformQuestion()).then(({data}) => {
        question.value = new Question(data);
        resetResponse();
        state.loading.question = false;
    });
}

const groupId = computed(() => router.currentRoute.value.params.group || router.currentRoute.value.query.group);
if (groupId.value && (!$store.group?.id || $store.group.id !== groupId.value)) {
    getGroup(router.currentRoute.value.query.group).then(({data}) => {
        $store.group = data;
        console.log($store.group);
    });
}
const isPublished = computed(() => !!question.value?.get("groups_count"));

const editRoute = computed(() => {
    let name = "questions.edit";
    let params = {question: question.value?.id};
    let query = router.currentRoute.value.query;
    if (groupId.value) {
        name = "groups." + name;
        params.group = groupId.value;
        query.group = groupId.value;
    }
    console.log({name, params, query});
    return {name, params, query};
});

const backRoute = computed(() => {
    return router.currentRoute.value.query.from === "form" && !isPublished.value ? editRoute.value : null;
});

const handleEditAttempt = () => {
    console.log(editRoute);
    if (isPublished.value && !$user.hasRole("super-admin")) {
        confirm.require({
            header: "Editing a published question",
            message: "This question has already been published. Would you like to duplicate it?",
            acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
            rejectClass: "text-red-600 px-2 py-1 rounded-lg hover:brightness-110",
            accept: async () => {
                const {data} = await duplicateQuestion(question.value.id);
                toast.add({
                    severity: "success",
                    summary: "Duplicated",
                    detail: "Question duplicated successfully.",
                    life: 3000,
                });
                await router.push(editRoute.value);
            },
        });
        return;
    }

    router.push(editRoute.value);
};

const launch = () => {
    if (router.currentRoute.value.query.group) {
        state.show.launchToGroupDrawer = true;
    } else {
        state.show.actionsDrawer = true;
    }
};
</script>
<template>
    <div>
        <div class="m-container relative mx-auto max-w-7xl">
            <div class="" v-if="router.currentRoute.value.query.launch">
                <LaunchHeader class="mb-4" @cancel="cancel">
                    <div class="flex items-end">
                        <p>Review your post, then hit the launch button</p>
                    </div>
                </LaunchHeader>
            </div>
            <div class="mb-3">
                <PageTitle title="Post preview" :route="backRoute">
                    <div class="ml-auto flex items-center gap-1">
                        <Button
                            :icon="{icon: 'prime:pencil'}"
                            class="border bg-stone-50 !p-2 text-xs shadow-sm sm:!px-3 sm:!py-1 dark:border-stone-700 dark:bg-stone-900"
                            @click="handleEditAttempt"
                        >
                            <span class="hidden sm:inline">Edit</span>
                        </Button>
                        <!--                        <RouterLink :to="{name: 'questions.create'}" class="ml-auto">-->
                        <!--                            <Button-->
                        <!--                                :icon="{icon: 'mdi:plus'}"-->
                        <!--                                class="border bg-stone-50 !p-2 text-xs shadow-sm sm:!px-3 sm:!py-1 dark:border-stone-700 dark:bg-stone-900"-->
                        <!--                            >-->
                        <!--                                <span class="hidden text-nowrap sm:inline">Add another question</span>-->
                        <!--                            </Button>-->
                        <!--                        </RouterLink>-->
                    </div>
                </PageTitle>
            </div>
            <PageLoading v-if="state.loading.question" class="mt-16" />
            <div v-if="question" class="mb-20">
                <div class="mb-4">
                    <div class="mb-4 flex w-full grow items-center" v-if="question.group">
                        <RouterLink
                            :to="{name: 'feed', params: {group: question.group_id}}"
                            class="grow rounded-lg !p-0"
                        >
                            <div class="mr-auto flex items-center gap-2">
                                <Avatar
                                    :label="question.group.icon ? undefined : question.group.initials"
                                    :image="question.group.icon?.url"
                                    class="mr--2 h-8 w-8"
                                    :class="[
                                        `bg-${question.group.color?.class}`,
                                        question.group.color?.class === 'white' ? 'text-black' : 'text-white',
                                    ]"
                                />
                                <h6 class="text--sm font-bold hover:underline">{{ question.group.name }}</h6>
                            </div>
                        </RouterLink>
                    </div>
                    <!--                    <div class="mb-1 flex flex-wrap items-center">-->
                    <!--                        <div-->
                    <!--                            class="flex flex-none items-center gap-1 text-sm font-medium text-stone-500 dark:text-stone-400"-->
                    <!--                        >-->
                    <!--                            <Icon icon="mdi:calendar-start-outline" width="16" />-->
                    <!--                            <span>-->
                    <!--                                {{ $date(question.publish_to).format("DD/MM/YYYY HH:mm") }}-->
                    <!--                            </span>-->
                    <!--                        </div>-->
                    <!--                        <div-->
                    <!--                            class="ml-auto flex flex-none items-center gap-1 text-sm font-medium text-stone-500 dark:text-stone-400"-->
                    <!--                        >-->
                    <!--                            <span>-->
                    <!--                                {{ $date(question.publish_to).format("DD/MM/YYYY HH:mm") }}-->
                    <!--                            </span>-->
                    <!--                            <Icon icon="mdi:calendar-end-outline" width="16" />-->
                    <!--                        </div>-->
                    <!--                    </div>-->
                    <h3
                        class="prose prose-stone mb-2 w-full max-w-full dark:prose-invert"
                        v-html="Str.of(md.render(question.get('title'))).toString()"
                    ></h3>
                    <!--                <TagList class="mt-2" :tags="question.tags" />-->
                </div>
                <div v-if="question.stimuli.length" ref="stimulusBlock">
                    <h3 class="mb-1 font-bold text-stone-500 dark:text-stone-400">Stimulus</h3>
                    <div class="mb-8 border-b pb-3 dark:border-stone-500" v-auto-animate>
                        <StimulusDisplay ref="stimulusDisplay" :stimulus="question.stimuli[0]" />
                        <div class="py-4" v-if="state.show.stimulusDetails">
                            <p class="text-xs font-bold uppercase">{{ question.stimuli[0].type.name }}</p>
                            <h2 class="break-words font-bold">{{ question.stimuli[0].title }}</h2>
                            <p class="break-words text-sm">{{ question.stimuli[0].description }}</p>
                        </div>
                        <div class="mt-2 flex justify-center">
                            <button
                                type="button"
                                class="flex items-center gap-1 text-xs"
                                @click="state.show.stimulusDetails = !state.show.stimulusDetails"
                            >
                                <Icon
                                    :icon="
                                        state.show.stimulusDetails ? 'akar-icons:chevron-up' : 'akar-icons:chevron-down'
                                    "
                                />
                                {{ state.show.stimulusDetails ? "Hide" : "Show" }} details
                            </button>
                        </div>
                    </div>
                </div>
                <template v-if="question.type.slug !== 'post'">
                    <div v-auto-animate>
                        <div class="mb-8" ref="responseBlock">
                            <div v-auto-animate>
                                <div v-if="question.id">
                                    <Component
                                        :is="QuestionComponents[question.type.slug]"
                                        :question="question"
                                        v-model="response.data"
                                        :disabled="true"
                                    />
                                </div>
                                <div>
                                    <Button
                                        type="submit"
                                        icon="material-symbols:save-outline"
                                        class="mt-4 w-full bg-stone-800 px-4 py-2 font-bold text-white dark:bg-stone-200 dark:text-stone-950"
                                        disabled
                                    >
                                        Preview only
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="question.type.slug === 'text'" class="mb-8">
                        <h3 class="mb-4 font-bold text-stone-500 lg:px-0">
                            {{ question.params.allow_voting ? "Vote on responses" : "Responses" }}
                        </h3>
                        <QuestionResponsesVoting ref="responsesTable" class="mb-6" :question="question" sort="date" />
                    </div>
                </template>
                <div v-if="question.params.allow_comments" class="mb-6">
                    <h3 class="mb-1 font-bold text-stone-500 lg:px-0">Comments</h3>
                    <div>
                        <Comments :model="question" model-type="question" :disabled="true" />
                        <!--                        <div v-else class="p-8">-->
                        <!--                            <p class="text-center text-stone-500">-->
                        <!--                                You must response the question before you can comment.-->
                        <!--                            </p>-->
                        <!--                        </div>-->
                    </div>
                </div>
                <template v-if="!['voice', 'text', 'post'].includes(question.type.slug) && question.params.show_charts">
                    <h3 class="mb-1 font-bold text-stone-500 lg:px-0">Charts</h3>
                    <div class="mb-6">
                        <QuestionResponsesChart
                            v-if="['multiple_choice', 'ranking', 'slider', 'weighting'].includes(question.type.slug)"
                            :question="question"
                        />
                        <QuestionResponsesWordCloud :question="question" v-if="question.type.slug === 'word'" />
                    </div>
                </template>
                <template v-if="question.params.show_responses && !['text', 'post'].includes(question.type.slug)">
                    <h3 class="mb-1 font-bold text-stone-500 lg:px-0">Responses</h3>
                    <div class="rounded-lg bg-stone-100 p-3 dark:bg-stone-900">
                        <QuestionResponsesTable :question="question" />
                    </div>
                </template>
            </div>
            <!--            <div-->
            <!--                class="m-container sticky bottom-16 left-0 z-50 mx-auto flex w-full max-w-3xl flex-col justify-center gap-2"-->
            <!--            >-->
            <!--                <div v-if="question?.id" class="flex flex-col items-center">-->
            <!--                    <button-->
            <!--                        rounded-->
            <!--                        class="pointer-events-auto flex h-16 w-16 flex-col items-center justify-center rounded-full bg-askr-green text-white shadow-xl shadow-stone-600 lg:h-24 lg:w-24 dark:shadow-stone-950"-->
            <!--                        @click="launch"-->
            <!--                    >-->
            <!--                        <Icon icon="mingcute:send-plane-fill" class="h-8 w-8 lg:h-12 lg:w-12" />-->
            <!--                        <span class="text-xs font-semibold">LAUNCH</span>-->
            <!--                    </button>-->
            <!--                    <button class="mt-6 text-sm" @click="handleEditAttempt">Edit</button>-->
            <!--                </div>-->
            <!--            </div>-->
            <GroupDrawer :group="state.show.group" @hide="state.show.group = null" />
            <!--            <div-->
            <!--                class="fixed bottom-0 left-0 right-0 z-10 w-full border-t border-stone-950/10 bg-askr-green p-2 text-center text-xs font-bold text-white"-->
            <!--            >-->
            <!--                PREVIEW-->
            <!--            </div>-->
        </div>
        <QuestionActionsDrawer v-model:visible="state.show.actionsDrawer" :question="question" />
        <QuestionLaunchToGroupDrawer v-model:visible="state.show.launchToGroupDrawer" :question="question" />
    </div>
</template>
<style scoped></style>
